/**
 * Thin fetch wrapper for the Ordis Admin API (`/admin/*` endpoints).
 *
 * Every call attaches the Cognito Bearer token from `authService`. Non-2xx
 * responses are surfaced as a typed `AdminApiError` with `status`, `code`,
 * and the server-provided `error` message so pages can render a clear toast.
 *
 * CSV downloads (`groups/export`, `users/export`) bypass `fetchJson` and use
 * `downloadCsv` so the browser triggers its native Save-as dialog.
 */

import { authService } from '../../service/authService';

// --- Shared types matching the backend contract ---

export interface PolicyRecord {
  posix_group: string;
  department: string;
  description: string;
  allowed_operations: string[];
  allowed_account_ids: string[];
  prod_access: boolean;
  last_updated: string;
  change_id?: string;
}

export interface PolicySummary {
  posix_group: string;
  department: string;
  operation_count: number;
  account_count: number;
  prod_access: boolean;
  description: string;
  last_updated: string;
}

export interface UserRecord {
  user_alias: string;
  posix_groups: string[];
  last_updated: string;
  change_id?: string;
}

export interface AccountRecord {
  account_id: string;
  account_name: string;
  environment: string;
  department: string;
}

export interface TemplateRecord {
  template_name: string;
  description: string;
  operations: string[];
  is_system: boolean;
  last_updated: string;
  change_id?: string;
}

export interface DepartmentRecord {
  department_name: string;
  display_name: string;
  description: string;
  contact_email: string;
  last_updated: string;
  change_id?: string;
}

export interface AuditRecord {
  change_id: string;
  timestamp: string;
  actor_alias: string;
  action: string;
  target_type: string;
  target_id: string;
  before_state: string | null;
  after_state: string | null;
  request_id: string;
}

export interface DashboardPayload {
  total_groups: number;
  total_users: number;
  accounts_with_prod_access: number;
  admin_user_count: number;
  recently_updated_groups: Array<{
    posix_group: string;
    department: string;
    last_updated: string;
  }>;
  recent_audit_events: Array<Pick<AuditRecord, 'change_id' | 'timestamp' | 'actor_alias' | 'action' | 'target_type' | 'target_id'>>;
}

export interface Paginated<T> {
  items: T[];
  next_token: string | null;
  page_size: number;
}

/** A user-submitted bug/issue report (spec ordis-bug-report). */
export interface BugReport {
  report_id: string;
  user_alias: string;
  created_at: string;
  category: string;
  severity: string;
  title: string;
  description: string;
  status: string;
  session_id?: string;
  page_url?: string;
  user_agent?: string;
  stage?: string;
}

export interface DiffResponse {
  posix_group: string;
  before: PolicyRecord | null;
  after: PolicyRecord;
  diff: {
    mode: 'create' | 'update';
    operations_added: string[];
    operations_removed: string[];
    accounts_added: string[];
    accounts_removed: string[];
    department_changed: boolean;
    prod_access_changed: boolean;
    before_prod_access: boolean;
    after_prod_access: boolean;
  };
  affected_user_count: number;
  affected_users: string[];
}

export interface BulkImportSummary {
  dry_run: boolean;
  total_rows: number;
  successful_rows: number;
  failed_rows: number;
  row_errors?: Array<{ row_number: number; error: string }>;
  rows?: Array<{ user_alias: string; posix_groups: string[] }>;
  change_id?: string;
  error?: string;
}

export interface BulkDeleteSummary {
  requested: number;
  affected: number;
  fully_deleted: number;
  change_id: string;
}

// --- Error type ---

export class AdminApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// --- Internal fetch helper ---

function isLocalhost(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  );
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  if (isLocalhost()) {
    // Short-circuit so the UI can be exercised without a running backend.
    return Promise.reject(
      new AdminApiError(
        503,
        'Admin API is not reachable from localhost. Deploy to Beta/Gamma to test admin flows.',
        'local_dev',
      ),
    );
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (!headers['Content-Type'] && init?.body) {
    headers['Content-Type'] = 'application/json';
  }
  const token = authService.getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(path, { ...init, headers });
  if (response.status === 401) {
    authService.login();
    throw new AdminApiError(401, 'Authentication expired', 'unauthenticated');
  }

  let bodyText = '';
  try {
    bodyText = await response.text();
  } catch {
    bodyText = '';
  }

  if (!response.ok) {
    let payload: { error?: string; code?: string; details?: unknown } = {};
    try {
      payload = bodyText ? JSON.parse(bodyText) : {};
    } catch {
      /* swallow parse errors — fall through to generic message */
    }
    throw new AdminApiError(
      response.status,
      payload.error || bodyText || `Request failed with status ${response.status}`,
      payload.code,
      payload.details,
    );
  }

  if (!bodyText) {
    return undefined as unknown as T;
  }
  try {
    return JSON.parse(bodyText) as T;
  } catch {
    throw new AdminApiError(500, 'Invalid JSON response from admin API');
  }
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    out.set(k, String(v));
  }
  const s = out.toString();
  return s ? `?${s}` : '';
}

async function downloadCsv(path: string, filename: string): Promise<void> {
  if (isLocalhost()) {
    throw new AdminApiError(503, 'Admin API is not reachable from localhost', 'local_dev');
  }
  const token = authService.getAccessToken();
  const response = await fetch(path, {
    headers: {
      Accept: 'text/csv',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (response.status === 401) {
    authService.login();
    throw new AdminApiError(401, 'Authentication expired', 'unauthenticated');
  }
  if (!response.ok) {
    const bodyText = await response.text();
    let payload: { error?: string } = {};
    try {
      payload = JSON.parse(bodyText);
    } catch {
      /* swallow */
    }
    throw new AdminApiError(response.status, payload.error || `Export failed (${response.status})`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// --- Public API surface ---

export const adminApi = {
  // Dashboard
  getDashboard: (refresh = false) =>
    fetchJson<DashboardPayload>(`/admin/dashboard${qs({ refresh: refresh || undefined })}`),

  // Groups
  listGroups: (params: {
    page_size?: number;
    next_token?: string;
    search?: string;
    department?: string;
    prod_access?: string;
    operation?: string;
  } = {}) => fetchJson<Paginated<PolicySummary>>(`/admin/groups${qs(params)}`),
  getGroup: (name: string) => fetchJson<PolicyRecord>(`/admin/groups/${encodeURIComponent(name)}`),
  createGroup: (body: Partial<PolicyRecord> & { cloned_from?: string }) =>
    fetchJson<PolicyRecord>('/admin/groups', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateGroup: (name: string, body: Partial<PolicyRecord> & { confirm_lockout_risk?: boolean }) =>
    fetchJson<PolicyRecord>(`/admin/groups/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteGroup: (name: string) =>
    fetchJson<{ posix_group: string; deleted: true; change_id: string }>(
      `/admin/groups/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),
  previewGroup: (name: string, body: Partial<PolicyRecord>) =>
    fetchJson<DiffResponse>(`/admin/groups/${encodeURIComponent(name)}/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  exportGroups: () => downloadCsv('/admin/groups/export', 'rbac-groups.csv'),

  // Users
  listUsers: (params: {
    page_size?: number;
    next_token?: string;
    search?: string;
    group?: string;
  } = {}) => fetchJson<Paginated<UserRecord>>(`/admin/users${qs(params)}`),
  getUser: (alias: string) => fetchJson<UserRecord>(`/admin/users/${encodeURIComponent(alias)}`),
  upsertUser: (alias: string, body: Partial<UserRecord>) =>
    fetchJson<UserRecord>(`/admin/users/${encodeURIComponent(alias)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteUser: (alias: string) =>
    fetchJson<{ user_alias: string; deleted: true; change_id: string }>(
      `/admin/users/${encodeURIComponent(alias)}`,
      { method: 'DELETE' },
    ),
  bulkImportUsers: (body: {
    csv?: string;
    rows?: Array<{ user_alias: string; posix_groups: string[] }>;
    dry_run?: boolean;
  }) =>
    fetchJson<BulkImportSummary>('/admin/users/bulk-import', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  bulkDeleteUsers: (body: { user_aliases: string[]; posix_group?: string }) =>
    fetchJson<BulkDeleteSummary>('/admin/users/bulk-delete', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  exportUsers: () => downloadCsv('/admin/users/export', 'rbac-users.csv'),

  // Accounts
  listAccounts: () =>
    fetchJson<{ accounts: AccountRecord[]; count: number }>('/admin/accounts'),

  // Audit
  listAudit: (params: {
    page_size?: number;
    next_token?: string;
    actor?: string;
    action?: string;
    target?: string;
    target_type?: string;
    from?: string;
    to?: string;
  } = {}) => fetchJson<Paginated<AuditRecord>>(`/admin/audit${qs(params)}`),

  // Bug reports (spec ordis-bug-report)
  listBugReports: (params: {
    page_size?: number;
    next_token?: string;
    user?: string;
    category?: string;
    severity?: string;
    status?: string;
    from?: string;
    to?: string;
  } = {}) => fetchJson<Paginated<BugReport>>(`/admin/bug-reports${qs(params)}`),

  // Templates
  listTemplates: () =>
    fetchJson<{ templates: TemplateRecord[]; count: number }>('/admin/templates'),
  getTemplate: (name: string) =>
    fetchJson<TemplateRecord>(`/admin/templates/${encodeURIComponent(name)}`),
  createTemplate: (body: Partial<TemplateRecord>) =>
    fetchJson<TemplateRecord>('/admin/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateTemplate: (name: string, body: Partial<TemplateRecord>) =>
    fetchJson<TemplateRecord>(`/admin/templates/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteTemplate: (name: string) =>
    fetchJson<{ template_name: string; deleted: true; change_id: string }>(
      `/admin/templates/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),

  // Departments
  listDepartments: () =>
    fetchJson<{ departments: DepartmentRecord[]; count: number }>('/admin/departments'),
  getDepartment: (name: string) =>
    fetchJson<DepartmentRecord>(`/admin/departments/${encodeURIComponent(name)}`),
  createDepartment: (body: Partial<DepartmentRecord>) =>
    fetchJson<DepartmentRecord>('/admin/departments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateDepartment: (name: string, body: Partial<DepartmentRecord>) =>
    fetchJson<DepartmentRecord>(`/admin/departments/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  deleteDepartment: (name: string) =>
    fetchJson<{ department_name: string; deleted: true; change_id: string }>(
      `/admin/departments/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),
};

// --- Permissions probe (uses the existing /query endpoint's permission inquiry) ---

export interface PermissionsProbe {
  allowedOperations: string[];
  departments: string[];
  productionAccess: boolean;
  accountCount: number;
}

// Module-level cache for ``probePermissions``. Both ``TopBar`` (via
// ``useAdminCapability``) and ``AdminGate`` call this on mount, which
// without caching means every page load fires two ``POST /query``
// requests and each one invokes Bedrock. Caching the ``Promise`` (not
// the resolved value) makes concurrent mounts share a single in-flight
// request and also serves the same result to all callers within the
// TTL window. Failure evicts the cache so the next caller retries.
const PROBE_CACHE_TTL_MS = 5 * 60 * 1000;
let _probeCache: Promise<PermissionsProbe | null> | null = null;
let _probeCacheAt = 0;

/** Test-only: reset the probe cache. Not exported in the public barrel. */
export function _resetProbeCacheForTests(): void {
  _probeCache = null;
  _probeCacheAt = 0;
}

async function _probePermissionsUncached(): Promise<PermissionsProbe | null> {
  if (isLocalhost()) return null;
  const token = authService.getAccessToken();
  if (!token) return null;
  // Intentionally no try/catch here. Previously this function swallowed
  // every error and resolved with ``null``, which made the outer
  // ``probePermissions().catch(...)`` wrapper dead code: a transient
  // network failure cached ``null`` for the full 5-minute TTL and hid
  // the Admin nav from legitimate admins until the cache expired. By
  // letting errors propagate, the outer wrapper can evict the cache
  // so the next caller retries, and the component-level ``try/catch``
  // in ``AdminGate`` / ``useAdminCapability`` can fall back to a
  // ``denied`` / ``false`` state without leaving the UI stuck on a
  // loading spinner.
  const response = await fetch('/query', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ input: 'what can I do?' }),
  });
  if (!response.ok) return null;
  const data = await response.json();
  const permissions = data?.permissions;
  if (!permissions || !Array.isArray(permissions.allowedOperations)) {
    return null;
  }
  return permissions as PermissionsProbe;
}

/**
 * Ask the Query Resolver "what can I do?" to discover whether the signed-in
 * user has the `admin` operation. This is how the UI decides whether to show
 * the admin nav link.
 *
 * The probe goes through ``/query``, which short-circuits the Bedrock
 * invocation to a deterministic permissions summary when the input
 * matches the \"what can I do?\" inquiry pattern. Even so, the round
 * trip is expensive, so results are cached at the module level for
 * :data:`PROBE_CACHE_TTL_MS`. A future cleanup could replace this with
 * a dedicated ``/admin/whoami`` endpoint that skips the LLM layer
 * entirely.
 */
export function probePermissions(): Promise<PermissionsProbe | null> {
  const now = Date.now();
  if (_probeCache && now - _probeCacheAt < PROBE_CACHE_TTL_MS) {
    return _probeCache;
  }
  _probeCacheAt = now;
  const pending = _probePermissionsUncached().catch((err) => {
    // Evict on failure so subsequent callers can retry rather than
    // being stuck with a cached rejection for 5 minutes.
    if (_probeCache === pending) {
      _probeCache = null;
      _probeCacheAt = 0;
    }
    throw err;
  });
  _probeCache = pending;
  return pending;
}

/**
 * True when the signed-in user has the `admin` operation enabled. The friendly
 * name on the `/query` response is "Manage RBAC policies (admin UI)" — we match
 * on that substring because the backend returns friendly names to the UI.
 */
export function hasAdminCapability(probe: PermissionsProbe | null): boolean {
  if (!probe) return false;
  return probe.allowedOperations.some((op) => op.toLowerCase().includes('admin'));
}
